/**
 * Decides, from /api/info, what the app shows first. Self-hosted: the app, as always. Hosted: the app when signed in,
 * otherwise the sign-in screen. If /api/info can't be reached (offline, server down), the app renders anyway, as it
 * did before there were editions, and shows its usual errors.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, type ReactNode } from 'react';
import { setSessionRecovery } from '@/api/client';
import { serverInfoQuery, useServerInfo } from '@/api/hooks';
import { SignInScreen, StartupScreen } from './SignInScreen';

export function AuthGate({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const info = useServerInfo();
  const hosted = info.data?.edition === 'hosted';

  useEffect(() => {
    // Concurrent 401s share one /api/info request (TanStack dedupes in-flight fetches of the same query).
    setSessionRecovery(async () => {
      try {
        const fresh = await queryClient.fetchQuery({ ...serverInfoQuery, staleTime: 0 });
        return fresh.account != null;
      } catch {
        return false;
      }
    });
    return () => setSessionRecovery(null);
  }, [queryClient]);

  if (info.isPending) return <StartupScreen />;
  if (hosted && !info.data?.account) return <SignInScreen />;
  return <>{children}</>;
}
